import React from 'react'
import { formatRelativeValue } from '../../../utils/formatters'
import type { RelativeValueBreakdown } from '../../../hooks/useRelativeValue'

interface RelativeValueCellProps {
  aircraftId: number
  cellBorderClass: string
  relativeValues: Map<number, number | null>
  relativeValueBreakdowns: Map<number, RelativeValueBreakdown>
  missingABB: Set<number>
}

/**
 * Renders the Relative Value column cell with optional breakdown details.
 */
export function RelativeValueCell(props: RelativeValueCellProps) {
  const { aircraftId, cellBorderClass, relativeValues, relativeValueBreakdowns, missingABB } = props

  const value = relativeValues.get(aircraftId)
  const breakdown = relativeValueBreakdowns.get(aircraftId)

  const formatAdjustment = (val: number): React.ReactNode => {
    if (val === 0) return '$0'
    const absValue = Math.abs(val)
    const formatted = formatRelativeValue(absValue)
    if (val < 0) {
      return <span className="text-red-600">({formatted})</span>
    }
    return formatted
  }

  let content: React.ReactNode
  if (missingABB.has(aircraftId)) {
    content = <span className="text-orange-600 font-medium">ABB</span>
  } else if (value === null || value === undefined) {
    content = <span className="text-center block">-</span>
  } else if (breakdown) {
    content = (
      <div className="flex flex-col w-full leading-tight overflow-hidden">
        <div className="text-right font-bold mb-0.5 whitespace-nowrap">
          {formatRelativeValue(value)}
        </div>
        <div className="flex flex-col gap-0 w-full text-xs">
          <div className="flex flex-row justify-between items-baseline w-full gap-1">
            <span className="truncate text-left">ABB Base Value:</span>
            <span className="flex-shrink-0 text-right whitespace-nowrap">{breakdown.abbValue !== null ? formatRelativeValue(breakdown.abbValue) : '-'}</span>
          </div>
          <div className="flex flex-row justify-between items-baseline w-full gap-1">
            <span className="truncate text-left">Options:</span>
            <span className="flex-shrink-0 text-right whitespace-nowrap">{breakdown.optionsTotal !== 0 ? formatRelativeValue(breakdown.optionsTotal) : '-'}</span>
          </div>
          <div className="flex flex-row justify-between items-baseline w-full gap-1">
            <span className="truncate text-left">AFTT:</span>
            <span className="flex-shrink-0 text-right whitespace-nowrap">{formatAdjustment(breakdown.maintenanceAdjustment)}</span>
          </div>
          <div className="flex flex-row justify-between items-baseline w-full gap-1">
            <span className="truncate text-left">EMP:</span>
            <span className="flex-shrink-0 text-right whitespace-nowrap">{formatAdjustment(-breakdown.empCostAdjustment)}</span>
          </div>
        </div>
      </div>
    )
  } else {
    content = <span className="text-center block">{formatRelativeValue(value)}</span>
  }

  return (
    <td className={`px-2 py-2 text-sm text-gray-900 ${cellBorderClass} overflow-hidden`}>
      {content}
    </td>
  )
}

